import { Environment, ReturnValue, BreakSignal, ContinueSignal, ThrowSignal } from '../runtime/environment.js';
import { parse as acornParse, tsParse, tsxParse } from '../parser.js';
import { createMethodNotFoundError } from '../errors/enhanced-error.js';

const ASYNC_EXPRESSION_COMPLETION = Symbol('jslike.asyncExpressionCompletion');

function createAsyncExpressionCompletion(value) {
  return { [ASYNC_EXPRESSION_COMPLETION]: true, value };
}

function isAsyncExpressionCompletion(value) {
  return value && value[ASYNC_EXPRESSION_COMPLETION] === true;
}

function unwrapAsyncExpressionCompletion(value) {
  return isAsyncExpressionCompletion(value) ? value.value : value;
}

function isTypeScriptPath(sourcePath) {
  return typeof sourcePath === 'string' && /\.(ts|tsx|mts|cts)$/i.test(sourcePath);
}

function isTSXPath(sourcePath) {
  return typeof sourcePath === 'string' && /\.tsx$/i.test(sourcePath);
}

function parseModuleCode(code, sourcePath) {
  const parser = isTSXPath(sourcePath) ? tsxParse : isTypeScriptPath(sourcePath) ? tsParse : acornParse;
  return parser(code, {
    ecmaVersion: isTypeScriptPath(sourcePath) ? 'latest' : 2020,
    sourceType: 'module',
    locations: isTypeScriptPath(sourcePath)
  });
}

const TYPE_ONLY_DECLARATIONS = new Set([
  'TSTypeAliasDeclaration',
  'TSInterfaceDeclaration',
  'TSDeclareFunction'
]);

const TYPE_WRAPPER_EXPRESSIONS = new Set([
  'TSAsExpression',
  'TSTypeAssertion',
  'TSNonNullExpression',
  'TSSatisfiesExpression',
  'TSInstantiationExpression'
]);

function isTypeOnlyDeclaration(node) {
  return TYPE_ONLY_DECLARATIONS.has(node?.type) || node?.declare === true;
}

function isTypeWrapperExpression(node) {
  return TYPE_WRAPPER_EXPRESSIONS.has(node?.type);
}

function getTypeWrapperInnerExpression(node) {
  return node.expression;
}

function createUnsupportedTypeScriptRuntimeError(node) {
  return new Error(`Unsupported runtime TypeScript syntax: ${node.type}`);
}

function getPatternName(pattern) {
  if (!pattern) return undefined;
  if (pattern.type === 'Identifier') return pattern.name;
  if (pattern.type === 'AssignmentPattern') return getPatternName(pattern.left);
  if (pattern.type === 'TSParameterProperty') return getPatternName(pattern.parameter);
  return pattern.name;
}

function unwrapTSParameterProperty(param) {
  return param?.type === 'TSParameterProperty' ? param.parameter : param;
}

function collectRuntimeIdentifierReferences(node) {
  const references = new Set();
  const skipKeys = new Set([
    'type',
    'start',
    'end',
    'loc',
    'range',
    'raw',
    'typeAnnotation',
    'returnType',
    'typeParameters',
    'typeArguments',
    'implements'
  ]);

  const visitPatternDefaults = (pattern) => {
    if (!pattern || typeof pattern !== 'object') return;
    if (pattern.type === 'AssignmentPattern') {
      visit(pattern.right);
      visitPatternDefaults(pattern.left);
    } else if (pattern.type === 'ObjectPattern') {
      for (const property of pattern.properties || []) {
        visitPatternDefaults(property.value || property.argument);
      }
    } else if (pattern.type === 'ArrayPattern') {
      for (const element of pattern.elements || []) {
        visitPatternDefaults(element);
      }
    } else if (pattern.type === 'RestElement') {
      visitPatternDefaults(pattern.argument);
    } else if (pattern.type === 'TSParameterProperty') {
      visitPatternDefaults(pattern.parameter);
    }
  };

  const visitFunction = (fn) => {
    for (const param of fn.params || []) {
      visitPatternDefaults(param);
    }
    visit(fn.body);
  };

  const visitJSXName = (jsxName) => {
    if (!jsxName || typeof jsxName !== 'object') return;
    if (jsxName.type === 'JSXIdentifier') {
      if (/^[A-Z]/.test(jsxName.name)) {
        references.add(jsxName.name);
      }
    } else if (jsxName.type === 'JSXMemberExpression') {
      visitJSXName(jsxName.object);
    } else if (jsxName.type === 'JSXNamespacedName') {
      visitJSXName(jsxName.namespace);
    }
  };

  const visit = (current, parent = null, parentKey = null) => {
    if (!current || typeof current !== 'object') return;

    if (Array.isArray(current)) {
      for (const item of current) visit(item, parent, parentKey);
      return;
    }

    if (current.type?.startsWith('TS')) {
      if (isTypeWrapperExpression(current)) {
        visit(getTypeWrapperInnerExpression(current), current, 'expression');
      } else if (current.type === 'TSEnumDeclaration') {
        for (const member of current.members || []) {
          visit(member.initializer);
        }
      }
      return;
    }

    switch (current.type) {
      case 'Identifier':
        references.add(current.name);
        return;
      case 'ImportDeclaration':
        return;
      case 'ExportNamedDeclaration':
        if (current.declaration) {
          visit(current.declaration, current, 'declaration');
        } else if (current.exportKind !== 'type') {
          for (const specifier of current.specifiers || []) {
            if (specifier.exportKind !== 'type' && specifier.local?.name) {
              references.add(specifier.local.name);
            }
          }
        }
        return;
      case 'ExportDefaultDeclaration':
        visit(current.declaration, current, 'declaration');
        return;
      case 'VariableDeclarator':
        visitPatternDefaults(current.id);
        visit(current.init, current, 'init');
        return;
      case 'FunctionDeclaration':
        visitFunction(current);
        return;
      case 'FunctionExpression':
      case 'ArrowFunctionExpression':
        visitFunction(current);
        return;
      case 'ClassDeclaration':
      case 'ClassExpression':
        visit(current.superClass, current, 'superClass');
        visit(current.body, current, 'body');
        return;
      case 'MemberExpression':
      case 'OptionalMemberExpression':
        visit(current.object, current, 'object');
        if (current.computed) {
          visit(current.property, current, 'property');
        }
        return;
      case 'Property':
        if (current.computed) {
          visit(current.key, current, 'key');
        }
        visit(current.value, current, 'value');
        return;
      case 'MethodDefinition':
      case 'PropertyDefinition':
        if (current.computed) {
          visit(current.key, current, 'key');
        }
        if (!current.declare && !current.abstract) {
          visit(current.value, current, 'value');
        }
        return;
      case 'AssignmentPattern':
        visit(current.right, current, 'right');
        return;
      case 'RestElement':
        return;
      case 'ObjectPattern':
      case 'ArrayPattern':
        visitPatternDefaults(current);
        return;
      case 'JSXElement':
        visitJSXName(current.openingElement?.name);
        for (const child of current.children || []) {
          visit(child);
        }
        return;
      case 'JSXFragment':
        for (const child of current.children || []) {
          visit(child);
        }
        return;
      case 'JSXExpressionContainer':
        visit(current.expression, current, 'expression');
        return;
    }

    for (const [key, value] of Object.entries(current)) {
      if (skipKeys.has(key)) continue;
      visit(value, current, key);
    }
  };

  visit(node);
  return references;
}

function getExportName(nameNode) {
  return nameNode?.name ?? nameNode?.value;
}

function collectDeclarationExportNames(declaration) {
  const names = [];

  if (!declaration || isTypeOnlyDeclaration(declaration)) {
    return names;
  }

  if (declaration.type === 'FunctionDeclaration' ||
      declaration.type === 'ClassDeclaration' ||
      declaration.type === 'TSEnumDeclaration') {
    const name = getPatternName(declaration.id);
    if (name) names.push(name);
    return names;
  }

  if (declaration.type === 'VariableDeclaration') {
    for (const declarator of declaration.declarations || []) {
      const name = getPatternName(declarator.id);
      if (name) names.push(name);
    }
  }

  return names;
}

function collectStaticExportNames(moduleAst) {
  const names = new Set();

  for (const statement of moduleAst?.body || []) {
    if (statement.type === 'ExportDefaultDeclaration') {
      names.add('default');
      continue;
    }

    if (statement.type !== 'ExportNamedDeclaration' || statement.exportKind === 'type') {
      continue;
    }

    if (statement.declaration) {
      for (const name of collectDeclarationExportNames(statement.declaration)) {
        names.add(name);
      }
      continue;
    }

    for (const specifier of statement.specifiers || []) {
      if (specifier.exportKind === 'type') {
        continue;
      }

      const exportedName = getExportName(specifier.exported);
      if (exportedName) {
        names.add(exportedName);
      }
    }
  }

  return names;
}

function predeclareModuleExports(moduleAst, moduleExports) {
  for (const name of collectStaticExportNames(moduleAst)) {
    if (!Object.prototype.hasOwnProperty.call(moduleExports, name)) {
      moduleExports[name] = undefined;
    }
  }
}

export class Interpreter {
  constructor(globalEnv, options = {}) {
    this.globalEnv = globalEnv;
    this.moduleResolver = options.moduleResolver;
    this.moduleCache = new Map();  // Cache loaded modules
    this.moduleResolutionCache = options.moduleResolutionCache || new Map();
    this.moduleExports = {};  // Track exports in current module
    this.currentModulePath = options.currentModulePath;
    this.isTypeScriptModule = options.isTypeScriptModule || false;
    this.sourceCode = options.sourceCode || '';
    this.runtimeIdentifierReferences = null;
    this.abortSignal = options.abortSignal;
    this.executionController = options.executionController;
  }

  // Check if execution should be aborted (sync version)
  checkAbortSignal() {
    // Check controller first if available
    if (this.executionController) {
      this.executionController._checkAbortSync();
      return;
    }
    // Fall back to legacy abortSignal
    if (this.abortSignal && this.abortSignal.aborted) {
      const error = new Error('The operation was aborted');
      error.name = 'AbortError';
      throw error;
    }
  }

  // Checkpoint that returns a promise only when controller is present
  // When no controller, returns null to signal no await needed
  _getCheckpointPromise(node, env) {
    if (this.executionController) {
      this.executionController._setEnv(env);
      return this.executionController._checkpoint(node);
    } else {
      this.checkAbortSignal();
      return null;  // Signal that no await is needed
    }
  }

  async evaluateAsyncRawValue(node, env) {
    if (!node) return { value: undefined };

    if (node.type === 'ImportExpression') {
      return { value: this.evaluateImportExpression(node, env) };
    }

    if (node.type === 'CallExpression') {
      return await this.evaluateCallExpressionAsyncRawValue(node, env);
    }

    if (node.type === 'MemberExpression') {
      const obj = (await this.evaluateAsyncRawValue(node.object, env)).value;

      if (node.optional && (obj === null || obj === undefined)) {
        return { value: undefined };
      }

      if (obj === null || obj === undefined) {
        throw new TypeError(`Cannot read property of ${obj}`);
      }

      const prop = node.computed
        ? await this.evaluateAsync(node.property, env)
        : node.property.name;

      return { value: obj[prop] };
    }

    if (node.type === 'ChainExpression') {
      return await this.evaluateAsyncRawValue(node.expression, env);
    }

    if (node.type === 'ConditionalExpression') {
      const test = await this.evaluateAsync(node.test, env);
      return await this.evaluateAsyncRawValue(test ? node.consequent : node.alternate, env);
    }

    if (node.type === 'LogicalExpression') {
      const left = await this.evaluateAsync(node.left, env);
      if (node.operator === '&&') {
        return left ? await this.evaluateAsyncRawValue(node.right, env) : { value: left };
      }
      if (node.operator === '||') {
        return left ? { value: left } : await this.evaluateAsyncRawValue(node.right, env);
      }
      if (node.operator === '??') {
        return left !== null && left !== undefined
          ? { value: left }
          : await this.evaluateAsyncRawValue(node.right, env);
      }
    }

    if (node.type === 'SequenceExpression') {
      for (let i = 0; i < node.expressions.length - 1; i++) {
        await this.evaluateAsync(node.expressions[i], env);
      }
      return await this.evaluateAsyncRawValue(node.expressions[node.expressions.length - 1], env);
    }

    if (node.type === 'AssignmentExpression') {
      const value = (await this.evaluateAsyncRawValue(node.right, env)).value;

      if (node.left.type === 'Identifier') {
        const name = node.left.name;
        if (node.operator === '=') {
          if (env.has(name)) {
            env.set(name, value);
          } else {
            env.define(name, value);
          }
          return { value };
        }

        const current = env.get(name);
        const newValue = this.applyCompoundAssignment(node.operator, current, value);
        env.set(name, newValue);
        return { value: newValue };
      }

      if (node.left.type === 'MemberExpression') {
        const obj = await this.evaluateAsync(node.left.object, env);
        const prop = node.left.computed
          ? await this.evaluateAsync(node.left.property, env)
          : node.left.property.name;

        if (node.operator === '=') {
          obj[prop] = value;
          return { value };
        }

        const newValue = this.applyCompoundAssignment(node.operator, obj[prop], value);
        obj[prop] = newValue;
        return { value: newValue };
      }

      throw new Error('Invalid assignment target');
    }

    if (node.type === 'NewExpression') {
      return { value: this.evaluateNewExpression(node, env) };
    }

    if (['Literal', 'Identifier', 'ThisExpression', 'Super'].includes(node.type)) {
      return { value: this.evaluate(node, env) };
    }

    return { value: await this.evaluateAsync(node, env) };
  }

  async evaluateCallExpressionAsyncRawValue(node, env) {
    let thisContext = undefined;
    let callee;
    let objectName = null;
    let methodName = null;

    if (node.callee.type === 'MemberExpression') {
      thisContext = (await this.evaluateAsyncRawValue(node.callee.object, env)).value;
      if (node.callee.optional && (thisContext === null || thisContext === undefined)) {
        return { value: undefined };
      }
      const prop = node.callee.computed
        ? await this.evaluateAsync(node.callee.property, env)
        : node.callee.property.name;
      callee = thisContext[prop];

      methodName = prop;
      objectName = this.getExpressionName(node.callee.object);
    } else {
      callee = await this.evaluateAsync(node.callee, env);
    }

    if (node.optional && (callee === null || callee === undefined)) {
      return { value: undefined };
    }

    const rawArgs = [];
    for (const arg of node.arguments) {
      rawArgs.push((await this.evaluateAsyncRawValue(arg, env)).value);
    }
    const args = this.flattenSpreadArgs(rawArgs);

    if (typeof callee === 'function') {
      const value = thisContext !== undefined
        ? callee.call(thisContext, ...args)
        : callee(...args);
      return { value };
    } else if (callee && callee.__isFunction) {
      return { value: this.callUserFunction(callee, args, env, thisContext) };
    }

    if (objectName && methodName) {
      throw createMethodNotFoundError(objectName, methodName, thisContext);
    }

    throw new TypeError(`${node.callee.name || 'Expression'} is not a function`);
  }

  // Async evaluation for async functions - handles await expressions
  async evaluateAsync(node, env) {
    if (!node) return undefined;

    // Checkpoint - yields if paused, throws if aborted
    // Only await when there's actually a promise (controller present)
    const checkpointPromise = this._getCheckpointPromise(node, env);
    if (checkpointPromise) await checkpointPromise;

    if (isTypeOnlyDeclaration(node)) {
      return undefined;
    }

    if (node.type === 'TSExportAssignment') {
      throw createUnsupportedTypeScriptRuntimeError(node);
    }

    if (node.type === 'TSImportEqualsDeclaration') {
      return await this.evaluateTSImportEqualsDeclaration(node, env);
    }

    if (node.type === 'TSEnumDeclaration') {
      return this.evaluateTSEnumDeclaration(node, env);
    }

    if (node.type === 'TSModuleDeclaration') {
      if (node.declare) return undefined;
      throw createUnsupportedTypeScriptRuntimeError(node);
    }

    if (isTypeWrapperExpression(node)) {
      return await this.evaluateAsync(getTypeWrapperInnerExpression(node), env);
    }

    // Handle await expressions by actually awaiting the promise
    if (node.type === 'AwaitExpression') {
      const promise = await this.evaluateAsync(node.argument, env);
      return await promise;
    }

    if (node.type === 'ImportExpression') {
      return await this.evaluateImportExpression(node, env);
    }

    // For block statements, evaluate each statement async
    if (node.type === 'BlockStatement') {
      const blockEnv = new Environment(env);
      let result = undefined;
      for (const statement of node.body) {
        result = await this.evaluateAsync(statement, blockEnv);
        if (result instanceof ReturnValue || result instanceof ThrowSignal ||
            result instanceof BreakSignal || result instanceof ContinueSignal) {
          return result;
        }
      }
      return result;
    }

    // For expression statements, evaluate the expression async
    if (node.type === 'ExpressionStatement') {
      return createAsyncExpressionCompletion((await this.evaluateAsyncRawValue(node.expression, env)).value);
    }

    // For variable declarations with await in init
    if (node.type === 'VariableDeclaration') {
      for (const declarator of node.declarations) {
        const value = declarator.init
          ? (await this.evaluateAsyncRawValue(declarator.init, env)).value
          : undefined;

        const isConst = node.kind === 'const';
        if (declarator.id.type === 'Identifier') {
          env.define(declarator.id.name, value, isConst);
        } else if (declarator.id.type === 'ObjectPattern') {
          this.bindObjectPattern(declarator.id, value, env, isConst);
        } else if (declarator.id.type === 'ArrayPattern') {
          this.bindArrayPattern(declarator.id, value, env, isConst);
        }
      }
      return undefined;
    }

    // For Program nodes (evaluate all statements async)
    if (node.type === 'Program') {
      const previousReferences = this.runtimeIdentifierReferences;
      this.runtimeIdentifierReferences = collectRuntimeIdentifierReferences(node);
      let result = undefined;
      try {
        for (const statement of node.body) {
          result = await this.evaluateAsync(statement, env);
          // Handle top-level return and throw
          if (result instanceof ReturnValue || result instanceof ThrowSignal) {
            return result;
          }
        }
        return unwrapAsyncExpressionCompletion(result);
      } finally {
        this.runtimeIdentifierReferences = previousReferences;
      }
    }

    // For import declarations (always async)
    if (node.type === 'ImportDeclaration') {
      return await this.evaluateImportDeclaration(node, env);
    }

    // For export declarations
    if (node.type === 'ExportNamedDeclaration') {
      return this.evaluateExportNamedDeclaration(node, env);
    }

    if (node.type === 'ExportDefaultDeclaration') {
      return this.evaluateExportDefaultDeclaration(node, env);
    }

    // For return statements with await
    if (node.type === 'ReturnStatement') {
      const value = node.argument ? await this.evaluateAsync(node.argument, env) : undefined;
      return new ReturnValue(value);
    }

    // For binary/unary expressions that might contain awaits
    if (node.type === 'BinaryExpression') {
      const left = await this.evaluateAsync(node.left, env);
      const right = await this.evaluateAsync(node.right, env);
      return this.evaluateBinaryExpressionValues(node.operator, left, right);
    }

    // For call expressions (might be calling async functions)
    if (node.type === 'CallExpression') {
      const result = (await this.evaluateCallExpressionAsyncRawValue(node, env)).value;
      return await result;
    }

    // For chain expressions (optional chaining)
    if (node.type === 'ChainExpression') {
      return await this.evaluateAsync(node.expression, env);
    }

    // For member expressions in async context
    if (node.type === 'MemberExpression') {
      const obj = (await this.evaluateAsyncRawValue(node.object, env)).value;

      // Handle optional chaining
      if (node.optional && (obj === null || obj === undefined)) {
        return undefined;
      }

      if (obj === null || obj === undefined) {
        throw new TypeError(`Cannot read property of ${obj}`);
      }

      const prop = node.computed
        ? await this.evaluateAsync(node.property, env)
        : node.property.name;

      return obj[prop];
    }

    // For template literals with await expressions
    if (node.type === 'TemplateLiteral') {
      let result = '';
      for (let i = 0; i < node.quasis.length; i++) {
        result += node.quasis[i].value.cooked || node.quasis[i].value.raw;
        if (i < node.expressions.length) {
          const exprValue = await this.evaluateAsync(node.expressions[i], env);
          result += String(exprValue);
        }
      }
      return result;
    }

    if (node.type === 'TaggedTemplateExpression') {
      // 1. Evaluate tag function async (may contain awaits)
      let thisContext = undefined;
      let tagFunction;

      if (node.tag.type === 'MemberExpression') {
        thisContext = await this.evaluateAsync(node.tag.object, env);
        const prop = node.tag.computed
          ? await this.evaluateAsync(node.tag.property, env)
          : node.tag.property.name;
        tagFunction = thisContext[prop];
      } else {
        tagFunction = await this.evaluateAsync(node.tag, env);
      }

      // 2. Build strings array (synchronous - no awaits in quasis)
      const strings = [];
      const rawStrings = [];
      for (const quasi of node.quasi.quasis) {
        strings.push(quasi.value.cooked || quasi.value.raw);
        rawStrings.push(quasi.value.raw);
      }

      Object.defineProperty(strings, 'raw', {
        value: Object.freeze(rawStrings),
        writable: false,
        enumerable: false,
        configurable: false
      });
      Object.freeze(strings);

      // 3. Evaluate expressions async (may contain awaits)
      const values = [];
      for (const expr of node.quasi.expressions) {
        values.push(await this.evaluateAsync(expr, env));
      }

      // 4. Call tag function (may be async)
      if (typeof tagFunction === 'function') {
        if (thisContext !== undefined) {
          return await tagFunction.call(thisContext, strings, ...values);
        }
        return await tagFunction(strings, ...values);
      } else if (tagFunction && tagFunction.__isFunction) {
        return await this.callUserFunction(tagFunction, [strings, ...values], env, thisContext);
      }

      throw new TypeError('Tag must be a function');
    }

    // For logical expressions with async operands (await support)
    if (node.type === 'LogicalExpression') {
      const left = await this.evaluateAsync(node.left, env);

      if (node.operator === '&&') {
        return left ? await this.evaluateAsync(node.right, env) : left;
      } else if (node.operator === '||') {
        return left ? left : await this.evaluateAsync(node.right, env);
      } else if (node.operator === '??') {
        return left !== null && left !== undefined ? left : await this.evaluateAsync(node.right, env);
      }

      throw new Error(`Unknown logical operator: ${node.operator}`);
    }

    // For try-catch-finally with async operations
    if (node.type === 'TryStatement') {
      let result;

      try {
        result = await this.evaluateAsync(node.block, env);

        if (result instanceof ThrowSignal) {
          throw result.value;
        }
      } catch (error) {
        if (node.handler) {
          const catchEnv = new Environment(env);
          if (node.handler.param) {
            catchEnv.define(node.handler.param.name, error);
          }
          result = await this.evaluateAsync(node.handler.body, catchEnv);
        } else {
          throw error;
        }
      } finally {
        if (node.finalizer) {
          const finalResult = await this.evaluateAsync(node.finalizer, env);
          // If finally block throws or returns, it overrides the try/catch result
          if (finalResult instanceof ThrowSignal || finalResult instanceof ReturnValue) {
            return finalResult;
          }
        }
      }

      return result;
    }

    // For new expressions (async constructors)
    if (node.type === 'NewExpression') {
      const result = this.evaluateNewExpression(node, env);
      // If it's a promise, await it
      if (result && typeof result.then === 'function') {
        return await result;
      }
      return result;
    }

    // For ForStatement with async body
    if (node.type === 'ForStatement') {
      const forEnv = new Environment(env);
      if (node.init) {
        await this.evaluateAsync(node.init, forEnv);
      }
      while (!node.test || await this.evaluateAsync(node.test, forEnv)) {
        // Checkpoint at each loop iteration (only await if controller present)
        const cp1 = this._getCheckpointPromise(node, forEnv);
        if (cp1) await cp1;
        const result = await this.evaluateAsync(node.body, forEnv);
        if (result instanceof BreakSignal) {
          break;
        }
        if (result instanceof ContinueSignal) {
          if (node.update) {
            await this.evaluateAsync(node.update, forEnv);
          }
          continue;
        }
        if (result instanceof ReturnValue || result instanceof ThrowSignal) {
          return result;
        }
        if (node.update) {
          await this.evaluateAsync(node.update, forEnv);
        }
      }
      return undefined;
    }

    // For ForOfStatement with async body
    if (node.type === 'ForOfStatement') {
      const forEnv = new Environment(env);
      const iterable = await this.evaluateAsync(node.right, forEnv);
      const declarator = node.left.declarations[0];
      const isConst = node.left.kind === 'const';

      for (const value of iterable) {
        // Checkpoint at each loop iteration (only await if controller present)
        const cp2 = this._getCheckpointPromise(node, forEnv);
        if (cp2) await cp2;
        const iterEnv = forEnv.extend();
        if (declarator.id.type === 'Identifier') {
          iterEnv.define(declarator.id.name, value, isConst);
        } else if (declarator.id.type === 'ArrayPattern') {
          this.bindArrayPattern(declarator.id, value, iterEnv, isConst);
        } else if (declarator.id.type === 'ObjectPattern') {
          this.bindObjectPattern(declarator.id, value, iterEnv, isConst);
        }
        const result = await this.evaluateAsync(node.body, iterEnv);
        if (result instanceof BreakSignal) {
          break;
        }
        if (result instanceof ContinueSignal) {
          continue;
        }
        if (result instanceof ReturnValue || result instanceof ThrowSignal) {
          return result;
        }
      }
      return undefined;
    }

    // For ForInStatement with async body
    if (node.type === 'ForInStatement') {
      const forEnv = new Environment(env);
      const obj = await this.evaluateAsync(node.right, forEnv);
      if (obj === null || obj === undefined) {
        throw new TypeError(`Cannot use 'in' operator to iterate over ${obj}`);
      }
      const varName = node.left.declarations[0].id.name;
      forEnv.define(varName, undefined);

      for (const key in obj) {
        // Checkpoint at each loop iteration (only await if controller present)
        const cp3 = this._getCheckpointPromise(node, forEnv);
        if (cp3) await cp3;
        forEnv.set(varName, key);
        const result = await this.evaluateAsync(node.body, forEnv);
        if (result instanceof BreakSignal) {
          break;
        }
        if (result instanceof ContinueSignal) {
          continue;
        }
        if (result instanceof ReturnValue || result instanceof ThrowSignal) {
          return result;
        }
      }
      return undefined;
    }

    // For WhileStatement with async body
    if (node.type === 'WhileStatement') {
      while (await this.evaluateAsync(node.test, env)) {
        // Checkpoint at each loop iteration (only await if controller present)
        const cp4 = this._getCheckpointPromise(node, env);
        if (cp4) await cp4;
        const result = await this.evaluateAsync(node.body, env);
        if (result instanceof BreakSignal) {
          break;
        }
        if (result instanceof ContinueSignal) {
          continue;
        }
        if (result instanceof ReturnValue || result instanceof ThrowSignal) {
          return result;
        }
      }
      return undefined;
    }

    // For DoWhileStatement with async body
    if (node.type === 'DoWhileStatement') {
      do {
        // Checkpoint at each loop iteration (only await if controller present)
        const cp5 = this._getCheckpointPromise(node, env);
        if (cp5) await cp5;
        const result = await this.evaluateAsync(node.body, env);
        if (result instanceof BreakSignal) {
          break;
        }
        if (result instanceof ContinueSignal) {
          continue;
        }
        if (result instanceof ReturnValue || result instanceof ThrowSignal) {
          return result;
        }
      } while (await this.evaluateAsync(node.test, env));
      return undefined;
    }

    // For IfStatement with async branches
    if (node.type === 'IfStatement') {
      const test = await this.evaluateAsync(node.test, env);
      if (test) {
        return await this.evaluateAsync(node.consequent, env);
      } else if (node.alternate) {
        return await this.evaluateAsync(node.alternate, env);
      }
      return undefined;
    }

    // For SwitchStatement with async cases
    if (node.type === 'SwitchStatement') {
      const discriminant = await this.evaluateAsync(node.discriminant, env);
      let matched = false;

      for (const switchCase of node.cases) {
        if (!matched && switchCase.test) {
          const testValue = await this.evaluateAsync(switchCase.test, env);
          if (testValue === discriminant) {
            matched = true;
          }
        } else if (!switchCase.test) {
          matched = true;
        }

        if (matched) {
          for (const statement of switchCase.consequent) {
            const result = await this.evaluateAsync(statement, env);
            if (result instanceof BreakSignal) {
              return undefined;
            }
            if (result instanceof ReturnValue || result instanceof ThrowSignal) {
              return result;
            }
          }
        }
      }
      return undefined;
    }

    // For ConditionalExpression (ternary) with async operands
    if (node.type === 'ConditionalExpression') {
      const test = await this.evaluateAsync(node.test, env);
      return test
        ? await this.evaluateAsync(node.consequent, env)
        : await this.evaluateAsync(node.alternate, env);
    }

    // For AssignmentExpression with async value
    if (node.type === 'AssignmentExpression') {
      const value = (await this.evaluateAsyncRawValue(node.right, env)).value;

      if (node.left.type === 'Identifier') {
        const name = node.left.name;
        if (node.operator === '=') {
          if (env.has(name)) {
            env.set(name, value);
          } else {
            env.define(name, value);
          }
          return value;
        } else {
          const current = env.get(name);
          const newValue = this.applyCompoundAssignment(node.operator, current, value);
          env.set(name, newValue);
          return newValue;
        }
      } else if (node.left.type === 'MemberExpression') {
        const obj = await this.evaluateAsync(node.left.object, env);
        const prop = node.left.computed
          ? await this.evaluateAsync(node.left.property, env)
          : node.left.property.name;

        if (node.operator === '=') {
          obj[prop] = value;
          return value;
        } else {
          const newValue = this.applyCompoundAssignment(node.operator, obj[prop], value);
          obj[prop] = newValue;
          return newValue;
        }
      }
      throw new Error('Invalid assignment target');
    }

    // For UnaryExpression with async argument
    if (node.type === 'UnaryExpression') {
      if (node.operator === 'delete' && node.argument.type === 'MemberExpression') {
        const obj = await this.evaluateAsync(node.argument.object, env);
        const prop = node.argument.computed
          ? await this.evaluateAsync(node.argument.property, env)
          : node.argument.property.name;
        return delete obj[prop];
      }
      const argument = await this.evaluateAsync(node.argument, env);
      switch (node.operator) {
        case '+': return +argument;
        case '-': return -argument;
        case '!': return !argument;
        case '~': return ~argument;
        case 'typeof':
          if (argument && argument.__isFunction) {
            return 'function';
          }
          return typeof argument;
        case 'void': return undefined;
        case 'delete': return true;
        default:
          throw new Error(`Unknown unary operator: ${node.operator}`);
      }
    }

    // For UpdateExpression with async member access
    if (node.type === 'UpdateExpression') {
      if (node.argument.type === 'Identifier') {
        const name = node.argument.name;
        const current = env.get(name);
        const numericCurrent = (current === null || current === undefined) ? 0 : Number(current);
        const newValue = node.operator === '++' ? numericCurrent + 1 : numericCurrent - 1;
        env.set(name, newValue);
        return node.prefix ? newValue : numericCurrent;
      } else if (node.argument.type === 'MemberExpression') {
        const obj = await this.evaluateAsync(node.argument.object, env);
        if (obj === null || obj === undefined) {
          throw new TypeError(
            `Cannot read properties of ${obj} (reading '${
              node.argument.computed
                ? await this.evaluateAsync(node.argument.property, env)
                : node.argument.property.name
            }')`
          );
        }
        const prop = node.argument.computed
          ? await this.evaluateAsync(node.argument.property, env)
          : node.argument.property.name;
        let current = obj[prop];
        const numericCurrent = (current === null || current === undefined) ? 0 : Number(current);
        const newValue = node.operator === '++' ? numericCurrent + 1 : numericCurrent - 1;
        obj[prop] = newValue;
        return node.prefix ? newValue : numericCurrent;
      }
      throw new Error('Invalid update expression target');
    }

    // For ArrayExpression with async elements
    if (node.type === 'ArrayExpression') {
      const result = [];
      for (const elem of node.elements) {
        if (!elem) {
          result.push(undefined);
        } else if (elem.type === 'SpreadElement') {
          const spreadValue = await this.evaluateAsync(elem.argument, env);
          if (Array.isArray(spreadValue)) {
            result.push(...spreadValue);
          } else if (typeof spreadValue[Symbol.iterator] === 'function') {
            result.push(...spreadValue);
          } else {
            throw new TypeError('Spread syntax requires an iterable');
          }
        } else {
          result.push((await this.evaluateAsyncRawValue(elem, env)).value);
        }
      }
      return result;
    }

    // For ObjectExpression with async values
    if (node.type === 'ObjectExpression') {
      const obj = {};
      for (const prop of node.properties) {
        if (prop.type === 'SpreadElement') {
          const spreadValue = await this.evaluateAsync(prop.argument, env);
          if (typeof spreadValue === 'object' && spreadValue !== null) {
            Object.assign(obj, spreadValue);
          }
        } else {
          const key = prop.key.type === 'Identifier' && !prop.computed
            ? prop.key.name
            : await this.evaluateAsync(prop.key, env);
          const value = prop.value ? (await this.evaluateAsyncRawValue(prop.value, env)).value : env.get(key);
          if (prop.method && prop.value.type === 'FunctionExpression') {
            obj[key] = (...args) => {
              const funcValue = this.evaluate(prop.value, env);
              return this.callUserFunction(funcValue, args, env);
            };
          } else {
            obj[key] = value;
          }
        }
      }
      return obj;
    }

    // For SequenceExpression with async expressions
    if (node.type === 'SequenceExpression') {
      let result;
      for (const expr of node.expressions) {
        result = await this.evaluateAsync(expr, env);
      }
      return result;
    }

    // For ThrowStatement with async argument
    if (node.type === 'ThrowStatement') {
      return new ThrowSignal(await this.evaluateAsync(node.argument, env));
    }

    // For FunctionDeclaration - define in environment
    if (node.type === 'FunctionDeclaration') {
      return this.evaluateFunctionDeclaration(node, env);
    }

    // For FunctionExpression/ArrowFunctionExpression - create function
    if (node.type === 'FunctionExpression' || node.type === 'ArrowFunctionExpression') {
      return this.evaluateFunctionExpression(node, env);
    }

    // For ClassDeclaration
    if (node.type === 'ClassDeclaration') {
      return this.evaluateClassDeclaration(node, env);
    }

    // For ClassExpression
    if (node.type === 'ClassExpression') {
      return this.evaluateClassExpression(node, env);
    }

    // JSX Support (async)
    if (node.type === 'JSXElement') {
      return await this.evaluateJSXElementAsync(node, env);
    }

    if (node.type === 'JSXFragment') {
      return await this.evaluateJSXFragmentAsync(node, env);
    }

    if (node.type === 'JSXExpressionContainer') {
      if (node.expression.type === 'JSXEmptyExpression') {
        return undefined;
      }
      return await this.evaluateAsync(node.expression, env);
    }

    if (node.type === 'JSXText') {
      return this.normalizeJSXText(node.value);
    }

    // For SpreadElement - return spread marker for flattenSpreadArgs
    if (node.type === 'SpreadElement') {
      const arg = await this.evaluateAsync(node.argument, env);
      if (Array.isArray(arg)) {
        return { __spread: true, __values: arg };
      }
      if (typeof arg === 'string') {
        return { __spread: true, __values: [...arg] };
      }
      if (arg !== null && arg !== undefined && typeof arg[Symbol.iterator] === 'function') {
        return { __spread: true, __values: [...arg] };
      }
      if (typeof arg === 'object' && arg !== null) {
        return { __spread: true, __values: Object.entries(arg) };
      }
      throw new TypeError('Spread syntax requires an iterable');
    }

    // Only leaf nodes should fall through to sync evaluate
    // These have no sub-expressions that could contain await
    if (['Literal', 'Identifier', 'BreakStatement', 'ContinueStatement',
         'EmptyStatement', 'ThisExpression', 'Super'].includes(node.type)) {
      return this.evaluate(node, env);
    }

    // Safety check - if we get here, we missed a node type
    throw new Error(`Unhandled node type in evaluateAsync: ${node.type}`);
  }

  evaluate(node, env) {
    if (!node) return undefined;

    // Check for abort signal before evaluating
    this.checkAbortSignal();

    if (isTypeOnlyDeclaration(node)) {
      return undefined;
    }

    if (node.type === 'TSExportAssignment') {
      throw createUnsupportedTypeScriptRuntimeError(node);
    }

    if (node.type === 'TSImportEqualsDeclaration') {
      return this.evaluateTSImportEqualsDeclaration(node, env);
    }

    if (node.type === 'TSEnumDeclaration') {
      return this.evaluateTSEnumDeclaration(node, env);
    }

    if (node.type === 'TSModuleDeclaration') {
      if (node.declare) return undefined;
      throw createUnsupportedTypeScriptRuntimeError(node);
    }

    if (isTypeWrapperExpression(node)) {
      return this.evaluate(getTypeWrapperInnerExpression(node), env);
    }

    switch (node.type) {
      case 'Program':
        return this.evaluateProgram(node, env);

      case 'Literal':
        // Handle regex literals
        if (node.regex) {
          return new RegExp(node.regex.pattern, node.regex.flags);
        }
        return node.value;

      case 'Identifier':
        return env.get(node.name);

      case 'BinaryExpression':
        return this.evaluateBinaryExpression(node, env);

      case 'UnaryExpression':
        return this.evaluateUnaryExpression(node, env);

      case 'UpdateExpression':
        return this.evaluateUpdateExpression(node, env);

      case 'AwaitExpression':
        return this.evaluateAwaitExpression(node, env);

      case 'AssignmentExpression':
        return this.evaluateAssignmentExpression(node, env);

      case 'LogicalExpression':
        return this.evaluateLogicalExpression(node, env);

      case 'ConditionalExpression':
        return this.evaluateConditionalExpression(node, env);

      case 'CallExpression':
        return this.evaluateCallExpression(node, env);

      case 'ImportExpression':
        return this.evaluateImportExpression(node, env);

      case 'MemberExpression':
        return this.evaluateMemberExpression(node, env);

      case 'ChainExpression':
        return this.evaluateChainExpression(node, env);

      case 'ArrayExpression':
        return this.evaluateArrayExpression(node, env);

      case 'ObjectExpression':
        return this.evaluateObjectExpression(node, env);

      case 'FunctionExpression':
      case 'ArrowFunctionExpression':
        return this.evaluateFunctionExpression(node, env);

      case 'NewExpression':
        return this.evaluateNewExpression(node, env);

      case 'ThisExpression':
        return this.evaluateThisExpression(node, env);

      case 'Super':
        return this.evaluateSuperExpression(node, env);

      case 'SequenceExpression':
        return this.evaluateSequenceExpression(node, env);

      case 'VariableDeclaration':
        return this.evaluateVariableDeclaration(node, env);

      case 'FunctionDeclaration':
        return this.evaluateFunctionDeclaration(node, env);

      case 'ImportDeclaration':
        return this.evaluateImportDeclaration(node, env);

      case 'ExportNamedDeclaration':
        return this.evaluateExportNamedDeclaration(node, env);

      case 'ExportDefaultDeclaration':
        return this.evaluateExportDefaultDeclaration(node, env);

      case 'BlockStatement':
        return this.evaluateBlockStatement(node, env);

      case 'ExpressionStatement':
        return this.evaluate(node.expression, env);

      case 'ReturnStatement':
        return new ReturnValue(node.argument ? this.evaluate(node.argument, env) : undefined);

      case 'IfStatement':
        return this.evaluateIfStatement(node, env);

      case 'WhileStatement':
        return this.evaluateWhileStatement(node, env);

      case 'DoWhileStatement':
        return this.evaluateDoWhileStatement(node, env);

      case 'ForStatement':
        return this.evaluateForStatement(node, env);

      case 'ForInStatement':
        return this.evaluateForInStatement(node, env);

      case 'ForOfStatement':
        return this.evaluateForOfStatement(node, env);

      case 'BreakStatement':
        return new BreakSignal();

      case 'ContinueStatement':
        return new ContinueSignal();

      case 'ThrowStatement':
        return new ThrowSignal(this.evaluate(node.argument, env));

      case 'TryStatement':
        return this.evaluateTryStatement(node, env);

      case 'SwitchStatement':
        return this.evaluateSwitchStatement(node, env);

      case 'EmptyStatement':
        return undefined;

      // ES6+ Features
      case 'TemplateLiteral':
        return this.evaluateTemplateLiteral(node, env);

      case 'TaggedTemplateExpression':
        return this.evaluateTaggedTemplateExpression(node, env);

      case 'ClassDeclaration':
        return this.evaluateClassDeclaration(node, env);

      case 'ClassExpression':
        return this.evaluateClassExpression(node, env);

      case 'MethodDefinition':
        return this.evaluateMethodDefinition(node, env);

      case 'SpreadElement':
        return this.evaluateSpreadElement(node, env);

      case 'RestElement':
        return this.evaluateRestElement(node, env);

      case 'ObjectPattern':
        return this.evaluateObjectPattern(node, env);

      case 'ArrayPattern':
        return this.evaluateArrayPattern(node, env);

      case 'AssignmentPattern':
        return this.evaluateAssignmentPattern(node, env);

      case 'Property':
        return this.evaluateProperty(node, env);

      case 'TSEnumDeclaration':
        return this.evaluateTSEnumDeclaration(node, env);

      // JSX Support
      case 'JSXElement':
        return this.evaluateJSXElement(node, env);

      case 'JSXFragment':
        return this.evaluateJSXFragment(node, env);

      case 'JSXExpressionContainer':
        if (node.expression.type === 'JSXEmptyExpression') {
          return undefined;
        }
        return this.evaluate(node.expression, env);

      case 'JSXText':
        return this.normalizeJSXText(node.value);

      default:
        throw new Error(`Unknown node type: ${node.type}`);
    }
  }

  evaluateProgram(node, env) {
    const previousReferences = this.runtimeIdentifierReferences;
    this.runtimeIdentifierReferences = collectRuntimeIdentifierReferences(node);
    let result = undefined;
    try {
      for (let i = 0; i < node.body.length; i++) {
        const statement = node.body[i];
        const isLast = i === node.body.length - 1;

        // Special case: Last statement is a BlockStatement that looks like object literal
        // Handle both shorthand { x, y } and full syntax { key: value, key2: value2 }
        if (isLast && statement.type === 'BlockStatement') {
          const objLiteral = this.tryConvertBlockToObjectLiteral(statement, env);
          if (objLiteral !== null) {
            return objLiteral;
          }
        }

        const statementResult = this.evaluate(statement, env);
        if (statementResult instanceof ReturnValue || statementResult instanceof ThrowSignal) {
          return statementResult;
        }
        result = statementResult;
      }
      return result;
    } finally {
      this.runtimeIdentifierReferences = previousReferences;
    }
  }

  // Try to convert a BlockStatement to an object literal
  // Returns null if the block doesn't look like an object literal
  tryConvertBlockToObjectLiteral(block, env) {
    if (block.body.length === 0) return null;

    // Check if it's shorthand syntax: { x, y }
    if (block.body.length === 1 && block.body[0].type === 'ExpressionStatement') {
      const expr = block.body[0].expression;

      // SequenceExpression of Identifiers: { x, y, z }
      if (expr.type === 'SequenceExpression' &&
          expr.expressions.every(e => e.type === 'Identifier')) {
        const obj = {};
        for (const identifier of expr.expressions) {
          obj[identifier.name] = env.get(identifier.name);
        }
        return obj;
      }

      // Single Identifier: { x }
      if (expr.type === 'Identifier') {
        const obj = {};
        obj[expr.name] = env.get(expr.name);
        return obj;
      }
    }

    // Check if it's labeled statements that look like object properties
    // Example: { first: first(arr), last: last(arr) }
    // This gets parsed as LabeledStatements in script mode
    const allLabeled = block.body.every(stmt => stmt.type === 'LabeledStatement');
    if (!allLabeled) return null;

    // Convert labeled statements to object properties
    const obj = {};
    for (const stmt of block.body) {
      const label = stmt.label.name;

      // The body of LabeledStatement should be ExpressionStatement
      if (stmt.body.type !== 'ExpressionStatement') {
        return null; // Not an object literal pattern
      }

      const value = this.evaluate(stmt.body.expression, env);
      obj[label] = value;
    }

    return obj;
  }

  evaluateBinaryExpression(node, env) {
    const left = this.evaluate(node.left, env);
    const right = this.evaluate(node.right, env);
    return this.evaluateBinaryExpressionValues(node.operator, left, right);
  }

  evaluateBinaryExpressionValues(operator, left, right) {
    switch (operator) {
      case '+': return left + right;
      case '-': return left - right;
      case '*': return left * right;
      case '/': return left / right;
      case '%': return left % right;
      case '**': return left ** right;
      case '<': return left < right;
      case '>': return left > right;
      case '<=': return left <= right;
      case '>=': return left >= right;
      case '==': return left == right;
      case '!=': return left != right;
      case '===': return left === right;
      case '!==': return left !== right;
      case '&': return left & right;
      case '|': return left | right;
      case '^': return left ^ right;
      case '<<': return left << right;
      case '>>': return left >> right;
      case '>>>': return left >>> right;
      case 'in': {
        // Check right operand is not null/undefined
        if (right === null || right === undefined) {
          throw new TypeError(
            'Cannot use "in" operator to search for property in null or undefined'
          );
        }
        // Coerce left operand to string/symbol for property key
        const key = String(left);
        return key in Object(right);
      }
      case 'instanceof': {
        // Check right operand is a constructor function or JSLike function
        if (typeof right !== 'function' && !(right && right.__isFunction)) {
          throw new TypeError(
            'Right-hand side of instanceof is not a constructor'
          );
        }
        // Primitives (null/undefined) always return false
        if (left === null || left === undefined) {
          return false;
        }
        // Special case: check if left is a JSLike function and right is Function constructor
        if (right === Function && left && left.__isFunction) {
          return true;
        }
        // Use JavaScript's instanceof for native objects
        if (typeof right === 'function') {
          return left instanceof right;
        }
        // For JSLike functions, check prototype chain
        return false;
      }
      default:
        throw new Error(`Unknown binary operator: ${operator}`);
    }
  }

  evaluateUnaryExpression(node, env) {
    const argument = this.evaluate(node.argument, env);

    switch (node.operator) {
      case '+': return +argument;
      case '-': return -argument;
      case '!': return !argument;
      case '~': return ~argument;
      case 'typeof':
        // JSLike functions should report as 'function'
        if (argument && argument.__isFunction) {
          return 'function';
        }
        return typeof argument;
      case 'void': return undefined;
      case 'delete':
        if (node.argument.type === 'MemberExpression') {
          const obj = this.evaluate(node.argument.object, env);
          const prop = node.argument.computed
            ? this.evaluate(node.argument.property, env)
            : node.argument.property.name;
          return delete obj[prop];
        }
        return true;
      default:
        throw new Error(`Unknown unary operator: ${node.operator}`);
    }
  }

  evaluateUpdateExpression(node, env) {
    if (node.argument.type === 'Identifier') {
      const name = node.argument.name;
      const current = env.get(name);
      // Wang feature: treat null/undefined as 0 for increment/decrement
      const numericCurrent = (current === null || current === undefined) ? 0 : Number(current);
      const newValue = node.operator === '++' ? numericCurrent + 1 : numericCurrent - 1;
      env.set(name, newValue);
      return node.prefix ? newValue : numericCurrent;
    } else if (node.argument.type === 'MemberExpression') {
      const obj = this.evaluate(node.argument.object, env);

      // Check for null/undefined object
      if (obj === null || obj === undefined) {
        throw new TypeError(
          `Cannot read properties of ${obj} (reading '${
            node.argument.computed
              ? this.evaluate(node.argument.property, env)
              : node.argument.property.name
          }')`
        );
      }

      const prop = node.argument.computed
        ? this.evaluate(node.argument.property, env)
        : node.argument.property.name;

      // Get current value and convert to number
      let current = obj[prop];
      // Wang feature: treat null/undefined as 0 for increment/decrement
      const numericCurrent = (current === null || current === undefined) ? 0 : Number(current);
      const newValue = node.operator === '++' ? numericCurrent + 1 : numericCurrent - 1;
      obj[prop] = newValue;

      return node.prefix ? newValue : numericCurrent;
    }
    throw new Error('Invalid update expression target');
  }

  evaluateAwaitExpression(node, env) {
    // Evaluate the argument (should be a Promise)
    const promise = this.evaluate(node.argument, env);

    // Return the promise - the caller must handle it
    // This is a simplified implementation that relies on the runtime being async
    return promise;
  }

  evaluateAssignmentExpression(node, env) {
    const value = this.evaluate(node.right, env);

    if (node.left.type === 'Identifier') {
      const name = node.left.name;

      if (node.operator === '=') {
        if (env.has(name)) {
          env.set(name, value);
        } else {
          env.define(name, value);
        }
        return value;
      } else {
        const current = env.get(name);
        const newValue = this.applyCompoundAssignment(node.operator, current, value);
        env.set(name, newValue);
        return newValue;
      }
    } else if (node.left.type === 'MemberExpression') {
      const obj = this.evaluate(node.left.object, env);
      const prop = node.left.computed
        ? this.evaluate(node.left.property, env)
        : node.left.property.name;

      if (node.operator === '=') {
        obj[prop] = value;
        return value;
      } else {
        const newValue = this.applyCompoundAssignment(node.operator, obj[prop], value);
        obj[prop] = newValue;
        return newValue;
      }
    }

    throw new Error('Invalid assignment target');
  }

  applyCompoundAssignment(operator, left, right) {
    // For numeric operators, coerce undefined to 0 (like JavaScript does for += with numbers)
    // But keep undefined for string concatenation
    const isNumericOp = operator !== '+=';
    const leftVal = (isNumericOp && left === undefined) ? 0 : left;

    switch (operator) {
      case '+=':
        // Special case: undefined + number should coerce undefined to 0
        if (left === undefined && typeof right === 'number') {
          return 0 + right;
        }
        return left + right;
      case '-=': return leftVal - right;
      case '*=': return leftVal * right;
      case '/=': return leftVal / right;
      case '%=': return leftVal % right;
      default: throw new Error(`Unknown assignment operator: ${operator}`);
    }
  }

  evaluateLogicalExpression(node, env) {
    const left = this.evaluate(node.left, env);

    if (node.operator === '&&') {
      return left ? this.evaluate(node.right, env) : left;
    } else if (node.operator === '||') {
      return left ? left : this.evaluate(node.right, env);
    } else if (node.operator === '??') {
      return left !== null && left !== undefined ? left : this.evaluate(node.right, env);
    }

    throw new Error(`Unknown logical operator: ${node.operator}`);
  }

  evaluateConditionalExpression(node, env) {
    const test = this.evaluate(node.test, env);
    return test
      ? this.evaluate(node.consequent, env)
      : this.evaluate(node.alternate, env);
  }

  evaluateCallExpression(node, env) {
    //  Determine thisContext for method calls
    let thisContext = undefined;
    let callee;
    let objectName = null;
    let methodName = null;

    if (node.callee.type === 'MemberExpression') {
      // For method calls like obj.method(), set this to obj
      thisContext = this.evaluate(node.callee.object, env);
      if (node.callee.optional && (thisContext === null || thisContext === undefined)) {
        return undefined;
      }
      const prop = node.callee.computed
        ? this.evaluate(node.callee.property, env)
        : node.callee.property.name;
      callee = thisContext[prop];

      // Capture names for enhanced error messages
      methodName = prop;
      objectName = this.getExpressionName(node.callee.object);
    } else {
      callee = this.evaluate(node.callee, env);
    }

    // Handle optional call - if optional and callee is null/undefined, return undefined
    if (node.optional && (callee === null || callee === undefined)) {
      return undefined;
    }

    const rawArgs = node.arguments.map(arg => this.evaluate(arg, env));
    const args = this.flattenSpreadArgs(rawArgs);

    if (typeof callee === 'function') {
      // Native JavaScript function or class method
      if (thisContext !== undefined) {
        return callee.call(thisContext, ...args);
      }
      return callee(...args);
    } else if (callee && callee.__isFunction) {
      // User-defined function - pass thisContext
      return this.callUserFunction(callee, args, env, thisContext);
    }

    // Throw enhanced error for member expression calls
    if (objectName && methodName) {
      throw createMethodNotFoundError(objectName, methodName, thisContext);
    }

    throw new TypeError(`${node.callee.name || 'Expression'} is not a function`);
  }

  // Helper to flatten spread elements in argument arrays
  flattenSpreadArgs(args) {
    const result = [];
    for (const arg of args) {
      if (arg && arg.__spread === true && Array.isArray(arg.__values)) {
        result.push(...arg.__values);
      } else {
        result.push(arg);
      }
    }
    return result;
  }

  // Helper to get a readable name for an expression (for error messages)
  getExpressionName(node) {
    if (!node) return 'object';

    switch (node.type) {
      case 'Identifier':
        return node.name;
      case 'ThisExpression':
        return 'this';
      case 'MemberExpression':
        const objName = this.getExpressionName(node.object);
        const propName = node.computed ? '[...]' : node.property.name;
        return `${objName}.${propName}`;
      default:
        return 'object';
    }
  }

  callUserFunction(func, args, callingEnv, thisContext = undefined) {
    // Extract metadata if function is wrapped
    const metadata = func.__metadata || func;
    const funcEnv = new Environment(metadata.closure);

    // Get function name for call stack tracking
    const funcName = metadata.name || func.name || 'anonymous';

    // Bind 'this': for arrow functions use captured this (lexical), for regular functions use call-site this
    if (metadata.isArrow) {
      // Arrow functions use lexically captured 'this', ignoring call-site 'this'
      if (metadata.capturedThis !== undefined) {
        funcEnv.define('this', metadata.capturedThis);
      }
    } else if (thisContext !== undefined) {
      // Regular functions use 'this' from the call site
      funcEnv.define('this', thisContext);
    }

    this.bindFunctionParameters(metadata.params, args, funcEnv);

    // Execute function body
    // If async, use async evaluation and return a promise
    if (metadata.async) {
      // Track call stack for async functions
      if (this.executionController) {
        this.executionController._pushCall(funcName);
      }
      return (async () => {
        try {
          if (metadata.expression) {
            // Arrow function with expression body
            const result = await this.evaluateAsync(metadata.body, funcEnv);
            // If the result is a ThrowSignal, throw the error
            if (result instanceof ThrowSignal) {
              throw result.value;
            }
            return result;
          } else {
            // Block statement body
            const result = await this.evaluateAsync(metadata.body, funcEnv);
            if (result instanceof ReturnValue) {
              return result.value;
            }
            // If the result is a ThrowSignal, throw the error
            if (result instanceof ThrowSignal) {
              throw result.value;
            }
            return undefined;
          }
        } finally {
          if (this.executionController) {
            this.executionController._popCall();
          }
        }
      })();
    } else {
      // Synchronous evaluation for non-async functions
      // Track call stack for sync functions
      if (this.executionController) {
        this.executionController._pushCall(funcName);
      }
      try {
        if (metadata.expression) {
          const result = this.evaluate(metadata.body, funcEnv);
          // If the result is a ThrowSignal, throw the error
          if (result instanceof ThrowSignal) {
            throw result.value;
          }
          return result;
        } else {
          const result = this.evaluate(metadata.body, funcEnv);
          if (result instanceof ReturnValue) {
            return result.value;
          }
          // If the result is a ThrowSignal, throw the error
          if (result instanceof ThrowSignal) {
            throw result.value;
          }
          return undefined;
        }
      } finally {
        if (this.executionController) {
          this.executionController._popCall();
        }
      }
    }
  }

  evaluateMemberExpression(node, env) {
    const obj = this.evaluate(node.object, env);

    // Handle optional chaining - if optional and obj is null/undefined, return undefined
    if (node.optional && (obj === null || obj === undefined)) {
      return undefined;
    }

    if (obj === null || obj === undefined) {
      throw new TypeError(`Cannot read property of ${obj}`);
    }

    const prop = node.computed
      ? this.evaluate(node.property, env)
      : node.property.name;

    return obj[prop];
  }

  evaluateChainExpression(node, env) {
    // ChainExpression is a wrapper for optional chaining expressions
    // It contains the actual expression (MemberExpression or CallExpression with optional: true)
    // We just evaluate the inner expression, which will handle the optional logic
    return this.evaluate(node.expression, env);
  }

  evaluateArrayExpression(node, env) {
    const result = [];
    for (const elem of node.elements) {
      if (!elem) {
        // Hole in array [1, , 3]
        result.push(undefined);
      } else if (elem.type === 'SpreadElement') {
        // Spread syntax [...arr]
        const spreadValue = this.evaluate(elem.argument, env);
        if (Array.isArray(spreadValue)) {
          result.push(...spreadValue);
        } else if (typeof spreadValue[Symbol.iterator] === 'function') {
          result.push(...spreadValue);
        } else {
          throw new TypeError('Spread syntax requires an iterable');
        }
      } else {
        result.push(this.evaluate(elem, env));
      }
    }
    return result;
  }

  evaluateObjectExpression(node, env) {
    const obj = {};
    for (const prop of node.properties) {
      if (prop.type === 'SpreadElement') {
        // Object spread {...other}
        const spreadValue = this.evaluate(prop.argument, env);
        if (typeof spreadValue === 'object' && spreadValue !== null) {
          Object.assign(obj, spreadValue);
        }
      } else {
        // Regular property or shorthand
        const key = prop.key.type === 'Identifier' && !prop.computed
          ? prop.key.name
          : this.evaluate(prop.key, env);

        // Handle shorthand properties {x} => {x: x}
        const value = prop.value ? this.evaluate(prop.value, env) : env.get(key);

        // Handle method shorthand: method() {}
        if (prop.method && prop.value.type === 'FunctionExpression') {
          obj[key] = (...args) => {
            const funcValue = this.evaluate(prop.value, env);
            return this.callUserFunction(funcValue, args, env);
          };
        } else {
          obj[key] = value;
        }
      }
    }
    return obj;
  }

  getSourceForNode(node) {
    if (typeof this.sourceCode !== 'string' ||
        typeof node?.start !== 'number' ||
        typeof node?.end !== 'number') {
      return undefined;
    }
    return this.sourceCode.slice(node.start, node.end);
  }

  installFunctionToString(wrappedFunc, funcMetadata) {
    const nativeToString = Function.prototype.toString.bind(wrappedFunc);
    Object.defineProperty(wrappedFunc, 'toString', {
      configurable: true,
      writable: true,
      value() {
        return funcMetadata.source || nativeToString();
      }
    });
  }

  evaluateFunctionExpression(node, env) {
    const isArrow = node.type === 'ArrowFunctionExpression';

    // For arrow functions, capture 'this' lexically from the enclosing scope
    let capturedThis;
    if (isArrow) {
      try {
        capturedThis = env.get('this');
      } catch (e) {
        // 'this' not defined in enclosing scope, leave undefined
        capturedThis = undefined;
      }
    }

    const funcMetadata = {
      __isFunction: true,
      params: node.params,
      body: node.body,
      closure: env,
      expression: isArrow && node.expression,
      async: node.async || false,
      isArrow: isArrow,
      capturedThis: isArrow ? capturedThis : undefined,
      source: this.getSourceForNode(node)
    };

    // Wrap in actual JavaScript function so it can be called by native code
    const interpreter = this;

    // Create async or sync wrapper based on function type
    // Note: using regular function (not arrow) to capture 'this' for method calls
    const wrappedFunc = funcMetadata.async
      ? async function(...args) {
          return await interpreter.callUserFunction(funcMetadata, args, funcMetadata.closure, this);
        }
      : function(...args) {
          return interpreter.callUserFunction(funcMetadata, args, funcMetadata.closure, this);
        };

    // Preserve metadata for JSLike's internal use
    wrappedFunc.__isFunction = true;
    wrappedFunc.__metadata = funcMetadata;
    this.installFunctionToString(wrappedFunc, funcMetadata);

    return wrappedFunc;
  }

  evaluateNewExpression(node, env) {
    const constructor = this.evaluate(node.callee, env);
    const rawArgs = node.arguments.map(arg => this.evaluate(arg, env));
    const args = this.flattenSpreadArgs(rawArgs);

    // Handle user-defined functions (including async and arrow functions)
    if (constructor && constructor.__isFunction) {
      const result = this.callUserFunction(constructor, args, env);
      // If result is a promise (async function), return it directly
      // The async context will handle it
      if (result && typeof result.then === 'function') {
        return result.then(res => {
          if (res && typeof res === 'object') {
            return res;
          }
          return {};
        });
      }
      // If the function returns an object, use it; otherwise create a new object
      if (result && typeof result === 'object') {
        return result;
      }
      // For arrow/async functions that don't return an object, create one
      return {};
    }

    if (typeof constructor === 'function') {
      // For native functions and classes, try to construct
      // Arrow functions and async functions can't be constructed with 'new' in JavaScript,
      // but if they return an object, we can use that
      try {
        return new constructor(...args);
      } catch (err) {
        // If construction fails (e.g., arrow function, async function),
        // try calling it normally and see if it returns an object
        if (err.message && err.message.includes('not a constructor')) {
          const result = constructor(...args);
          // If result is a promise, handle it
          if (result && typeof result.then === 'function') {
            return result.then(res => {
              if (res && typeof res === 'object') {
                return res;
              }
              throw new TypeError(`Type mismatch in new expression: ${node.callee.name || 'Expression'} is not a constructor`);
            });
          }
          if (result && typeof result === 'object') {
            return result;
          }
          throw new TypeError(`Type mismatch in new expression: ${node.callee.name || 'Expression'} is not a constructor`);
        }
        throw err;
      }
    }

    throw new TypeError(`Type mismatch in new expression: ${node.callee.name || 'Expression'} is not a constructor`);
  }

  evaluateThisExpression(node, env) {
    try {
      return env.get('this');
    } catch (e) {
      // 'this' not defined in current scope
      return undefined;
    }
  }

  evaluateSuperExpression(node, env) {
    // Super is used in class methods to access parent class
    try {
      return env.get('super');
    } catch (e) {
      throw new ReferenceError("'super' keyword is unexpected here");
    }
  }

  evaluateSequenceExpression(node, env) {
    let result;
    for (const expr of node.expressions) {
      result = this.evaluate(expr, env);
    }
    return result;
  }

  evaluateVariableDeclaration(node, env) {
    const isConst = node.kind === 'const';

    for (const declarator of node.declarations) {
      const value = declarator.init
        ? this.evaluate(declarator.init, env)
        : undefined;

      // Handle destructuring patterns
      if (declarator.id.type === 'ObjectPattern') {
        this.bindObjectPattern(declarator.id, value, env, isConst);
      } else if (declarator.id.type === 'ArrayPattern') {
        this.bindArrayPattern(declarator.id, value, env, isConst);
      } else {
        env.define(declarator.id.name, value, isConst);
      }
    }
    return undefined;
  }

  evaluateTSEnumDeclaration(node, env) {
    const enumObject = {};
    let nextNumericValue = 0;

    for (const member of node.members) {
      const memberName = member.id.name ?? member.id.value;
      let value;

      if (member.initializer) {
        value = this.evaluate(member.initializer, env);
      } else {
        value = nextNumericValue;
      }

      enumObject[memberName] = value;

      if (typeof value === 'number') {
        enumObject[value] = memberName;
        nextNumericValue = value + 1;
      } else {
        nextNumericValue = undefined;
      }
    }

    env.define(node.id.name, enumObject, false);
    return undefined;
  }

  bindObjectPattern(pattern, value, env, isConst = false) {
    if (value === null || value === undefined) {
      throw new TypeError('Cannot destructure undefined or null');
    }

    for (const prop of pattern.properties) {
      if (prop.type === 'RestElement') {
        // Handle rest properties {...rest}
        const assignedKeys = pattern.properties
          .filter(p => p.type !== 'RestElement')
          .map(p => p.key.name || p.key.value);
        const restObj = {};
        for (const key in value) {
          if (!assignedKeys.includes(key)) {
            restObj[key] = value[key];
          }
        }
        env.define(prop.argument.name, restObj, isConst);
      } else {
        const key = prop.key.name || prop.key.value;
        const propValue = value[key];

        if (prop.value.type === 'Identifier') {
          env.define(prop.value.name, propValue, isConst);
        } else if (prop.value.type === 'AssignmentPattern') {
          // Handle default values
          const finalValue = propValue !== undefined
            ? propValue
            : this.evaluate(prop.value.right, env);
          this.bindPattern(prop.value.left, finalValue, env, isConst);
        } else if (prop.value.type === 'ObjectPattern') {
          this.bindObjectPattern(prop.value, propValue, env, isConst);
        } else if (prop.value.type === 'ArrayPattern') {
          this.bindArrayPattern(prop.value, propValue, env, isConst);
        }
      }
    }
  }

  bindArrayPattern(pattern, value, env, isConst = false) {
    if (!Array.isArray(value)) {
      throw new TypeError('Cannot destructure non-iterable');
    }

    for (let i = 0; i < pattern.elements.length; i++) {
      const element = pattern.elements[i];
      if (!element) continue; // Hole in pattern [a, , c]

      if (element.type === 'RestElement') {
        // Handle rest elements [...rest]
        const restValues = value.slice(i);
        env.define(element.argument.name, restValues, isConst);
        break;
      } else if (element.type === 'Identifier') {
        env.define(element.name, value[i], isConst);
      } else if (element.type === 'AssignmentPattern') {
        // Handle default values
        const finalValue = value[i] !== undefined
          ? value[i]
          : this.evaluate(element.right, env);
        this.bindPattern(element.left, finalValue, env, isConst);
      } else if (element.type === 'ObjectPattern') {
        this.bindObjectPattern(element, value[i], env, isConst);
      } else if (element.type === 'ArrayPattern') {
        this.bindArrayPattern(element, value[i], env, isConst);
      }
    }
  }

  bindPattern(pattern, value, env, isConst = false) {
    if (pattern.type === 'Identifier') {
      env.define(pattern.name, value, isConst);
    } else if (pattern.type === 'ObjectPattern') {
      this.bindObjectPattern(pattern, value, env, isConst);
    } else if (pattern.type === 'ArrayPattern') {
      this.bindArrayPattern(pattern, value, env, isConst);
    } else if (pattern.type === 'AssignmentPattern') {
      const finalValue = value !== undefined ? value : this.evaluate(pattern.right, env);
      this.bindPattern(pattern.left, finalValue, env, isConst);
    } else if (pattern.type === 'RestElement') {
      this.bindPattern(pattern.argument, value, env, isConst);
    }
  }

  bindFunctionParameters(params, args, env, thisContext = null) {
    for (let i = 0; i < params.length; i++) {
      const originalParam = params[i];
      const param = unwrapTSParameterProperty(originalParam);

      this.bindFunctionParameter(param, args[i], args.slice(i), env);

      if (originalParam.type === 'TSParameterProperty' && thisContext) {
        const propertyName = getPatternName(originalParam.parameter);
        if (propertyName) {
          thisContext[propertyName] = env.get(propertyName);
        }
      }

      if (param.type === 'RestElement') {
        break;
      }
    }
  }

  bindFunctionParameter(param, arg, restArgs, env) {
    if (param.type === 'Identifier') {
      this.bindPattern(param, arg, env);
      return;
    }

    if (param.type === 'AssignmentPattern') {
      const value = arg !== undefined ? arg : this.evaluate(param.right, env);
      this.bindPattern(param.left, value, env);
      return;
    }

    if (param.type === 'RestElement') {
      const target = param.argument;
      if (target.type === 'Identifier') {
        env.define(target.name, restArgs);
      } else if (target.type === 'ArrayPattern') {
        this.bindArrayPattern(target, restArgs, env);
      } else if (target.type === 'ObjectPattern') {
        this.bindObjectPattern(target, restArgs, env);
      }
      return;
    }

    if (param.type === 'ObjectPattern') {
      this.bindPattern(param, arg, env);
      return;
    }

    if (param.type === 'ArrayPattern') {
      this.bindPattern(param, arg, env);
      return;
    }

    env.define(param.name, arg);
  }

  evaluateFunctionDeclaration(node, env) {
    const funcMetadata = {
      __isFunction: true,
      params: node.params,
      body: node.body,
      closure: env,
      expression: false,
      async: node.async || false,
      source: this.getSourceForNode(node)
    };

    // Wrap in actual JavaScript function so it can be called by native code
    const interpreter = this;

    // Create async or sync wrapper based on function type
    // Note: using regular function (not arrow) to capture 'this' for method calls
    const wrappedFunc = funcMetadata.async
      ? async function(...args) {
          return await interpreter.callUserFunction(funcMetadata, args, funcMetadata.closure, this);
        }
      : function(...args) {
          return interpreter.callUserFunction(funcMetadata, args, funcMetadata.closure, this);
        };

    // Preserve metadata for JSLike's internal use
    wrappedFunc.__isFunction = true;
    wrappedFunc.__metadata = funcMetadata;
    this.installFunctionToString(wrappedFunc, funcMetadata);

    env.define(node.id.name, wrappedFunc);
    return undefined;
  }

  async evaluateImportDeclaration(node, env) {
    // Get module path from import source
    const modulePath = node.source.value;

    if (node.importKind === 'type' ||
        (node.specifiers.length > 0 && node.specifiers.every(specifier => specifier.importKind === 'type'))) {
      return undefined;
    }

    if (this.isTypeScriptModule &&
        node.specifiers.length > 0 &&
        node.specifiers.every(specifier => !this.isRuntimeImportSpecifier(specifier))) {
      return undefined;
    }

    const moduleExports = await this.loadModuleExports(modulePath);

    this.bindImportSpecifiers(node, env, modulePath, moduleExports);
    return undefined;
  }

  async evaluateImportExpression(node, env) {
    const modulePath = (await this.evaluateAsyncRawValue(node.source, env)).value;
    if (typeof modulePath !== 'string') {
      throw new TypeError('Dynamic import specifier must evaluate to a string');
    }

    return await this.loadModuleExports(modulePath);
  }

  async loadModuleExports(modulePath) {
    // Check if module resolver is configured
    if (!this.moduleResolver) {
      throw new Error('Module resolver not configured - cannot import modules');
    }

    const fromPath = this.currentModulePath;

    const resolutionCacheKey = `${fromPath || ''}\0${modulePath}`;
    let resolution;
    let resolvedPath = this.moduleResolutionCache.get(resolutionCacheKey);
    if (!resolvedPath && !modulePath.startsWith('.') && this.moduleCache.has(modulePath)) {
      resolvedPath = modulePath;
    }

    // Check if module is already cached
    let moduleExports;
    if (resolvedPath && this.moduleCache.has(resolvedPath)) {
      moduleExports = this.moduleCache.get(resolvedPath);
    } else {
      // Resolve first so relative imports can use importer context and cache by resolved path.
      resolution = await this.moduleResolver.resolve(modulePath, fromPath);
      if (!resolution) {
        throw new Error(`Cannot find module '${modulePath}'`);
      }

      resolvedPath = typeof resolution === 'string'
        ? modulePath
        : resolution.path || modulePath;
      this.moduleResolutionCache.set(resolutionCacheKey, resolvedPath);
      if (this.moduleCache.has(resolvedPath)) {
        return this.moduleCache.get(resolvedPath);
      }

      // Handle native module exports (for libraries like React)
      // If resolution has 'exports' property, use it directly without parsing
      if (resolution.exports) {
        moduleExports = resolution.exports;
        this.moduleCache.set(resolvedPath, moduleExports);
      } else {
        // Handle both old (string) and new (ModuleResolution) formats
        const moduleCode = typeof resolution === 'string' ? resolution : resolution.code;

        // Parse and execute module in its own environment
        const moduleAst = parseModuleCode(moduleCode, resolvedPath);
        const moduleEnv = new Environment(this.globalEnv);

        // Create a new interpreter for the module with shared module cache
        const moduleInterpreter = new Interpreter(this.globalEnv, {
          moduleResolver: this.moduleResolver,
          moduleResolutionCache: this.moduleResolutionCache,
          currentModulePath: resolvedPath,
          isTypeScriptModule: isTypeScriptPath(resolvedPath),
          abortSignal: this.abortSignal,
          executionController: this.executionController,
          sourceCode: moduleCode
        });
        moduleInterpreter.moduleCache = this.moduleCache;  // Share cache

        moduleExports = moduleInterpreter.moduleExports;
        predeclareModuleExports(moduleAst, moduleExports);
        this.moduleCache.set(resolvedPath, moduleExports);

        try {
          // Execute module and populate the cached exports object.
          await moduleInterpreter.evaluateAsync(moduleAst, moduleEnv);
        } catch (error) {
          this.moduleCache.delete(resolvedPath);
          throw error;
        }
      }
    }

    return moduleExports;
  }

  async evaluateTSImportEqualsDeclaration(node, env) {
    if (node.importKind === 'type') {
      return undefined;
    }

    const localName = node.id?.name;
    const moduleReference = node.moduleReference;

    if (moduleReference?.type !== 'TSExternalModuleReference') {
      throw createUnsupportedTypeScriptRuntimeError(node);
    }

    const modulePath = moduleReference.expression?.value;
    if (typeof modulePath !== 'string') {
      throw createUnsupportedTypeScriptRuntimeError(node);
    }

    const moduleExports = await this.loadModuleExports(modulePath);
    env.define(localName, moduleExports);

    if (node.isExport) {
      this.moduleExports[localName] = moduleExports;
    }

    return undefined;
  }

  bindImportSpecifiers(node, env, modulePath, moduleExports) {
    // Import specified bindings into current environment
    for (const specifier of node.specifiers) {
      if (!this.isRuntimeImportSpecifier(specifier)) {
        continue;
      }

      if (specifier.type === 'ImportSpecifier') {
        // Named import: import { foo, bar } from "module"
        const importedName = specifier.imported.name;
        const localName = specifier.local.name;

        if (!(importedName in moduleExports)) {
          throw new Error(`Module '${modulePath}' has no export '${importedName}'`);
        }

        env.defineLive(localName, () => moduleExports[importedName]);
      } else if (specifier.type === 'ImportDefaultSpecifier') {
        // Default import: import foo from "module"
        const localName = specifier.local.name;

        if (!('default' in moduleExports)) {
          throw new Error(`Module '${modulePath}' has no default export`);
        }

        env.defineLive(localName, () => moduleExports.default);
      } else if (specifier.type === 'ImportNamespaceSpecifier') {
        // Namespace import: import * as foo from "module"
        const localName = specifier.local.name;
        env.define(localName, moduleExports);
      }
    }

    return undefined;
  }

  isRuntimeImportSpecifier(specifier) {
    if (specifier.importKind === 'type') {
      return false;
    }

    if (!this.isTypeScriptModule) {
      return true;
    }

    const localName = specifier.local?.name;
    if (!localName || !this.runtimeIdentifierReferences) {
      return true;
    }

    return this.runtimeIdentifierReferences.has(localName);
  }

  evaluateExportNamedDeclaration(node, env) {
    if (node.exportKind === 'type' || isTypeOnlyDeclaration(node.declaration)) {
      return undefined;
    }

    // Handle export with declaration: export function foo() {} or export const x = 42
    if (node.declaration) {
      const result = this.evaluate(node.declaration, env);

      // Register exported names
      if (node.declaration.type === 'FunctionDeclaration') {
        // export function foo() {}
        const name = node.declaration.id.name;
        this.moduleExports[name] = env.get(name);
      } else if (node.declaration.type === 'VariableDeclaration') {
        // export const x = 42, y = 10
        for (const declarator of node.declaration.declarations) {
          const name = declarator.id.name;
          this.moduleExports[name] = env.get(name);
        }
      } else if (node.declaration.type === 'ClassDeclaration') {
        // export class Foo {}
        const name = node.declaration.id.name;
        this.moduleExports[name] = env.get(name);
      } else if (node.declaration.type === 'TSEnumDeclaration') {
        const name = node.declaration.id.name;
        this.moduleExports[name] = env.get(name);
      }

      return result;
    }

    // Handle export list: export { foo, bar }
    if (node.specifiers && node.specifiers.length > 0) {
      for (const specifier of node.specifiers) {
        if (specifier.exportKind === 'type') {
          continue;
        }

        const exportedName = specifier.exported.name;
        const localName = specifier.local.name;
        this.moduleExports[exportedName] = env.get(localName);
      }
    }

    return undefined;
  }

  evaluateExportDefaultDeclaration(node, env) {
    // Evaluate the default export expression/declaration
    let value;

    if (node.declaration.type === 'FunctionDeclaration' || node.declaration.type === 'ClassDeclaration') {
      // export default function foo() {} or export default class Foo {}
      value = this.evaluate(node.declaration, env);
      // If it has a name, it's also defined in the environment
      if (node.declaration.id) {
        value = env.get(node.declaration.id.name);
      }
    } else {
      // export default expression
      value = this.evaluate(node.declaration, env);
    }

    // Register as default export
    this.moduleExports.default = value;

    return undefined;
  }

  evaluateBlockStatement(node, env) {
    const blockEnv = new Environment(env);
    let result;

    for (const statement of node.body) {
      result = this.evaluate(statement, blockEnv);
      if (result instanceof ReturnValue ||
          result instanceof BreakSignal ||
          result instanceof ContinueSignal ||
          result instanceof ThrowSignal) {
        return result;
      }
    }

    return result;
  }

  evaluateIfStatement(node, env) {
    const test = this.evaluate(node.test, env);

    if (test) {
      return this.evaluate(node.consequent, env);
    } else if (node.alternate) {
      return this.evaluate(node.alternate, env);
    }

    return undefined;
  }

  evaluateWhileStatement(node, env) {
    let result;

    while (this.evaluate(node.test, env)) {
      result = this.evaluate(node.body, env);

      if (result instanceof BreakSignal) {
        break;
      }
      if (result instanceof ContinueSignal) {
        continue;
      }
      if (result instanceof ReturnValue || result instanceof ThrowSignal) {
        return result;
      }
    }

    return undefined;
  }

  evaluateDoWhileStatement(node, env) {
    let result;

    do {
      result = this.evaluate(node.body, env);

      if (result instanceof BreakSignal) {
        break;
      }
      if (result instanceof ContinueSignal) {
        continue;
      }
      if (result instanceof ReturnValue || result instanceof ThrowSignal) {
        return result;
      }
    } while (this.evaluate(node.test, env));

    return undefined;
  }

  evaluateForStatement(node, env) {
    const forEnv = new Environment(env);
    let result;

    if (node.init) {
      this.evaluate(node.init, forEnv);
    }

    while (!node.test || this.evaluate(node.test, forEnv)) {
      result = this.evaluate(node.body, forEnv);

      if (result instanceof BreakSignal) {
        break;
      }
      if (result instanceof ContinueSignal) {
        if (node.update) {
          this.evaluate(node.update, forEnv);
        }
        continue;
      }
      if (result instanceof ReturnValue || result instanceof ThrowSignal) {
        return result;
      }

      if (node.update) {
        this.evaluate(node.update, forEnv);
      }
    }

    return undefined;
  }

  evaluateForInStatement(node, env) {
    const forEnv = new Environment(env);
    const obj = this.evaluate(node.right, forEnv);
    let result;

    // Check for null or undefined - JavaScript throws TypeError
    if (obj === null || obj === undefined) {
      throw new TypeError(`Cannot use 'in' operator to iterate over ${obj}`);
    }

    // Get the variable name from the declaration
    const varName = node.left.declarations[0].id.name;

    // Define the variable once before the loop
    forEnv.define(varName, undefined);

    for (const key in obj) {
      // Update the variable value for each iteration
      forEnv.set(varName, key);
      result = this.evaluate(node.body, forEnv);

      if (result instanceof BreakSignal) {
        break;
      }
      if (result instanceof ContinueSignal) {
        continue;
      }
      if (result instanceof ReturnValue || result instanceof ThrowSignal) {
        return result;
      }
    }

    return undefined;
  }

  evaluateForOfStatement(node, env) {
    const forEnv = new Environment(env);
    const iterable = this.evaluate(node.right, forEnv);
    let result;

    const declarator = node.left.declarations[0];
    const isConst = node.left.kind === 'const';

    for (const value of iterable) {
      // Create a new child environment for each iteration to handle const properly
      const iterEnv = forEnv.extend();

      // Bind the value using the appropriate pattern
      if (declarator.id.type === 'Identifier') {
        iterEnv.define(declarator.id.name, value, isConst);
      } else if (declarator.id.type === 'ArrayPattern') {
        this.bindArrayPattern(declarator.id, value, iterEnv, isConst);
      } else if (declarator.id.type === 'ObjectPattern') {
        this.bindObjectPattern(declarator.id, value, iterEnv, isConst);
      }

      result = this.evaluate(node.body, iterEnv);

      if (result instanceof BreakSignal) {
        break;
      }
      if (result instanceof ContinueSignal) {
        continue;
      }
      if (result instanceof ReturnValue || result instanceof ThrowSignal) {
        return result;
      }
    }

    return undefined;
  }

  evaluateTryStatement(node, env) {
    let result;

    try {
      result = this.evaluate(node.block, env);

      if (result instanceof ThrowSignal) {
        throw result.value;
      }
    } catch (error) {
      if (node.handler) {
        const catchEnv = new Environment(env);
        if (node.handler.param) {
          catchEnv.define(node.handler.param.name, error);
        }
        result = this.evaluate(node.handler.body, catchEnv);
      } else {
        throw error;
      }
    } finally {
      if (node.finalizer) {
        const finalResult = this.evaluate(node.finalizer, env);
        // If finally block throws or returns, it overrides the try/catch result
        if (finalResult instanceof ThrowSignal || finalResult instanceof ReturnValue) {
          return finalResult;
        }
      }
    }

    return result;
  }

  evaluateSwitchStatement(node, env) {
    const discriminant = this.evaluate(node.discriminant, env);
    let matched = false;
    let result;

    for (const switchCase of node.cases) {
      // Check if this case matches (or if we're in fall-through mode)
      if (!matched && switchCase.test) {
        const testValue = this.evaluate(switchCase.test, env);
        if (testValue === discriminant) {
          matched = true;
        }
      } else if (!switchCase.test) {
        // Default case
        matched = true;
      }

      // Execute consequent if matched
      if (matched) {
        for (const statement of switchCase.consequent) {
          result = this.evaluate(statement, env);

          if (result instanceof BreakSignal) {
            return undefined;
          }
          if (result instanceof ReturnValue || result instanceof ThrowSignal) {
            return result;
          }
        }
      }
    }

    return undefined;
  }

  // ===== ES6+ Feature Implementations =====

  evaluateTemplateLiteral(node, env) {
    let result = '';
    for (let i = 0; i < node.quasis.length; i++) {
      result += node.quasis[i].value.cooked || node.quasis[i].value.raw;
      if (i < node.expressions.length) {
        const exprValue = this.evaluate(node.expressions[i], env);
        result += String(exprValue);
      }
    }
    return result;
  }

  evaluateTaggedTemplateExpression(node, env) {
    // 1. Evaluate the tag function, preserving 'this' context for member expressions
    let thisContext = undefined;
    let tagFunction;

    if (node.tag.type === 'MemberExpression') {
      // For method calls like obj.tag`...`, set this to obj
      thisContext = this.evaluate(node.tag.object, env);
      const prop = node.tag.computed
        ? this.evaluate(node.tag.property, env)
        : node.tag.property.name;
      tagFunction = thisContext[prop];
    } else {
      tagFunction = this.evaluate(node.tag, env);
    }

    // 2. Build the strings array from quasis (cooked values)
    const strings = [];
    const rawStrings = [];
    for (const quasi of node.quasi.quasis) {
      strings.push(quasi.value.cooked || quasi.value.raw);
      rawStrings.push(quasi.value.raw);
    }

    // 3. Add the raw property (frozen per ES6 spec)
    Object.defineProperty(strings, 'raw', {
      value: Object.freeze(rawStrings),
      writable: false,
      enumerable: false,
      configurable: false
    });
    Object.freeze(strings);

    // 4. Evaluate the embedded expressions
    const values = node.quasi.expressions.map(expr => this.evaluate(expr, env));

    // 5. Call the tag function with proper this context
    if (typeof tagFunction === 'function') {
      if (thisContext !== undefined) {
        return tagFunction.call(thisContext, strings, ...values);
      }
      return tagFunction(strings, ...values);
    } else if (tagFunction && tagFunction.__isFunction) {
      // User-defined function
      return this.callUserFunction(tagFunction, [strings, ...values], env, thisContext);
    }

    throw new TypeError('Tag must be a function');
  }

  evaluateClassDeclaration(node, env) {
    const className = node.id.name;
    const classFunc = this.createClass(node, env);
    env.define(className, classFunc);
    return undefined;
  }

  evaluateClassExpression(node, env) {
    return this.createClass(node, env);
  }

  createClass(node, env) {
    const className = node.id ? node.id.name : 'AnonymousClass';
    const superClass = node.superClass ? this.evaluate(node.superClass, env) : null;
    const interpreter = this; // Capture interpreter reference

    // Find constructor
    let constructor = null;
    const methods = {};
    const staticMethods = {};
    const instanceFields = [];

    for (const member of node.body.body) {
      if (member.type === 'MethodDefinition') {
        const methodName = member.key.name || member.key.value;
        const methodFunc = this.createMethodFunction(member.value, env, className);

        if (member.kind === 'constructor') {
          constructor = methodFunc;
        } else if (member.static) {
          staticMethods[methodName] = methodFunc;
        } else {
          methods[methodName] = methodFunc;
        }
      } else if (member.type === 'PropertyDefinition' && !member.static && !member.declare && !member.abstract) {
        instanceFields.push(member);
      }
    }

    // Create class constructor function
    const classConstructor = function(...args) {
      // Create instance
      const instance = Object.create(classConstructor.prototype);
      const result = interpreter.constructClassInto(classConstructor, instance, args, env);

      // Only use the returned object if it's an explicit return of an object (not the instance)
      if (result && result.__explicitReturn && result.value && typeof result.value === 'object' && result.value !== instance) {
        return result.value;
      }

      return instance;
    };

    // Store the constructor method on the classConstructor for super() to access
    if (constructor) {
      classConstructor.__constructor = constructor;
    }

    classConstructor.__instanceFields = instanceFields;
    classConstructor.__superClass = superClass;
    classConstructor.__constructInto = (instance, args) => {
      return interpreter.constructClassInto(classConstructor, instance, args, env);
    };

    // Set up prototype chain
    if (superClass) {
      classConstructor.prototype = Object.create(superClass.prototype);
      classConstructor.prototype.constructor = classConstructor;
    }

    // Add methods to prototype
    for (const [name, method] of Object.entries(methods)) {
      classConstructor.prototype[name] = function(...args) {
        const result = interpreter.callMethodFunction(method, this, args, env, superClass);
        if (result && typeof result.then === 'function') {
          return result.then(resolved => {
            if (resolved && resolved.__explicitReturn) {
              return resolved.value;
            }
            return resolved;
          });
        }
        // Unwrap explicit return marker
        if (result && result.__explicitReturn) {
          return result.value;
        }
        return result;
      };
    }

    // Add static methods
    for (const [name, method] of Object.entries(staticMethods)) {
      classConstructor[name] = function(...args) {
        const result = interpreter.callMethodFunction(method, classConstructor, args, env);
        if (result && typeof result.then === 'function') {
          return result.then(resolved => {
            if (resolved && resolved.__explicitReturn) {
              return resolved.value;
            }
            return resolved;
          });
        }
        // Unwrap explicit return marker
        if (result && result.__explicitReturn) {
          return result.value;
        }
        return result;
      };
    }

    classConstructor.__className = className;
    return classConstructor;
  }

  constructClassInto(classConstructor, instance, args, env) {
    const superClass = classConstructor.__superClass;
    const constructor = classConstructor.__constructor;
    let fieldsInitialized = false;

    const initializeOwnFields = () => {
      if (!fieldsInitialized) {
        this.initializeClassFields(classConstructor, instance, env);
        fieldsInitialized = true;
      }
    };

    if (!superClass) {
      initializeOwnFields();
      if (constructor) {
        return this.callMethodFunction(constructor, instance, args, env, null);
      }
      return undefined;
    }

    if (constructor) {
      const result = this.callMethodFunction(constructor, instance, args, env, superClass, initializeOwnFields);
      initializeOwnFields();
      return result;
    }

    this.initializeSuperClass(superClass, instance, args);
    initializeOwnFields();
    return undefined;
  }

  initializeSuperClass(superClass, instance, args) {
    if (superClass.__constructInto) {
      return superClass.__constructInto(instance, args);
    }

    // For native constructors like Error, use Reflect.construct and copy instance properties.
    const tempInstance = Reflect.construct(superClass, args, instance.constructor);
    Object.getOwnPropertyNames(tempInstance).forEach(name => {
      instance[name] = tempInstance[name];
    });
    return undefined;
  }

  initializeClassFields(classConstructor, instance, env) {
    for (const field of classConstructor.__instanceFields || []) {
      const fieldEnv = new Environment(env);
      fieldEnv.define('this', instance);
      if (classConstructor.__superClass) {
        fieldEnv.define('super', this.createSuperBinding(classConstructor.__superClass, instance, false));
      }
      const name = this.getClassFieldName(field, fieldEnv);
      instance[name] = field.value ? this.evaluate(field.value, fieldEnv) : undefined;
    }
  }

  getClassFieldName(field, env) {
    if (field.computed) {
      return this.evaluate(field.key, env);
    }
    if (field.key.type === 'Identifier' || field.key.type === 'PrivateIdentifier') {
      return field.key.name;
    }
    return field.key.value;
  }

  createSuperBinding(superClass, thisContext, allowConstructor = false, afterSuper = null) {
    const superConstructor = (...superArgs) => {
      if (!allowConstructor) {
        throw new ReferenceError("'super' keyword is unexpected here");
      }
      const result = this.initializeSuperClass(superClass, thisContext, superArgs);
      if (afterSuper) {
        afterSuper();
      }
      return result && result.__explicitReturn ? result.value : undefined;
    };

    superConstructor.__isSuperConstructor = true;
    superConstructor.__superClass = superClass;

    return new Proxy(superConstructor, {
      get(target, prop, receiver) {
        if (prop in target) {
          return Reflect.get(target, prop, receiver);
        }

        const prototype = superClass?.prototype;
        if (!prototype) {
          return undefined;
        }

        const value = Reflect.get(prototype, prop, thisContext);
        return typeof value === 'function' ? value.bind(thisContext) : value;
      }
    });
  }

  createMethodFunction(funcNode, env, className) {
    const func = {
      __isFunction: true,
      __params: funcNode.params,
      __body: funcNode.body,
      __env: env,
      __className: className,
      __async: funcNode.async || false
    };
    return func;
  }

  callMethodFunction(methodFunc, thisContext, args, env, superClass = null, afterSuper = null) {
    const funcEnv = new Environment(methodFunc.__env || env);

    // Bind 'this'
    funcEnv.define('this', thisContext);

    // Bind 'super' if superClass exists
    if (superClass) {
      funcEnv.define('super', this.createSuperBinding(superClass, thisContext, true, afterSuper));
    }

    this.bindFunctionParameters(methodFunc.__params, args, funcEnv, thisContext);

    if (methodFunc.__async) {
      return (async () => {
        const result = await this.evaluateAsync(methodFunc.__body, funcEnv);

        if (result instanceof ReturnValue) {
          return { __explicitReturn: true, value: result.value };
        }

        if (result instanceof ThrowSignal) {
          throw result.value;
        }

        return result;
      })();
    }

    const result = this.evaluate(methodFunc.__body, funcEnv);

    if (result instanceof ReturnValue) {
      // Mark that this was an explicit return for constructor handling
      return { __explicitReturn: true, value: result.value };
    }

    // If the result is a ThrowSignal, throw the error
    if (result instanceof ThrowSignal) {
      throw result.value;
    }

    // Return implicit result (for arrow function expressions)
    return result;
  }

  evaluateMethodDefinition(node, env) {
    // This is handled by class creation
    return undefined;
  }

  evaluateSpreadElement(node, env) {
    const arg = this.evaluate(node.argument, env);
    if (Array.isArray(arg)) {
      return { __spread: true, __values: arg };
    }
    // Strings are iterable - spread into characters
    if (typeof arg === 'string') {
      return { __spread: true, __values: [...arg] };
    }
    // Handle other iterables (like Set, Map, etc.)
    if (arg !== null && arg !== undefined && typeof arg[Symbol.iterator] === 'function') {
      return { __spread: true, __values: [...arg] };
    }
    if (typeof arg === 'object' && arg !== null) {
      return { __spread: true, __values: Object.entries(arg) };
    }
    throw new TypeError('Spread syntax requires an iterable');
  }

  evaluateRestElement(node, env) {
    // Handled during parameter binding
    return undefined;
  }

  evaluateObjectPattern(node, env) {
    // Handled during destructuring
    return undefined;
  }

  evaluateArrayPattern(node, env) {
    // Handled during destructuring
    return undefined;
  }

  evaluateAssignmentPattern(node, env) {
    // Handled during parameter binding with defaults
    return undefined;
  }

  evaluateProperty(node, env) {
    // Already handled in evaluateObjectExpression
    return undefined;
  }

  // ===== JSX Support =====

  evaluateJSXElement(node, env) {
    const createElement = this.getCreateElement(env);
    const { type, props } = this.evaluateJSXOpeningElement(node.openingElement, env);
    const children = this.evaluateJSXChildren(node.children, env);

    if (children.length === 0) {
      return createElement(type, props);
    } else if (children.length === 1) {
      return createElement(type, props, children[0]);
    }
    return createElement(type, props, ...children);
  }

  evaluateJSXFragment(node, env) {
    const createElement = this.getCreateElement(env);
    const Fragment = this.getFragment(env);
    const children = this.evaluateJSXChildren(node.children, env);

    if (children.length === 0) {
      return createElement(Fragment, null);
    } else if (children.length === 1) {
      return createElement(Fragment, null, children[0]);
    }
    return createElement(Fragment, null, ...children);
  }

  evaluateJSXOpeningElement(node, env) {
    const type = this.evaluateJSXElementName(node.name, env);
    const props = {};

    for (const attr of node.attributes) {
      if (attr.type === 'JSXAttribute') {
        const name = attr.name.type === 'JSXIdentifier'
          ? attr.name.name
          : `${attr.name.namespace.name}:${attr.name.name.name}`;
        const value = attr.value
          ? this.evaluateJSXAttributeValue(attr.value, env)
          : true;
        props[name] = value;
      } else if (attr.type === 'JSXSpreadAttribute') {
        Object.assign(props, this.evaluate(attr.argument, env));
      }
    }

    return { type, props: Object.keys(props).length > 0 ? props : null };
  }

  evaluateJSXElementName(node, env) {
    if (node.type === 'JSXIdentifier') {
      const name = node.name;
      // Lowercase = intrinsic ('div'), Uppercase = component
      if (name[0] === name[0].toLowerCase()) {
        return name;
      }
      return env.get(name);
    } else if (node.type === 'JSXMemberExpression') {
      const object = this.evaluateJSXElementName(node.object, env);
      return object[node.property.name];
    } else if (node.type === 'JSXNamespacedName') {
      return `${node.namespace.name}:${node.name.name}`;
    }
    throw new Error(`Unknown JSX element name type: ${node.type}`);
  }

  evaluateJSXAttributeValue(node, env) {
    if (node.type === 'Literal') return node.value;
    if (node.type === 'JSXExpressionContainer') {
      return this.evaluate(node.expression, env);
    }
    if (node.type === 'JSXElement') return this.evaluateJSXElement(node, env);
    if (node.type === 'JSXFragment') return this.evaluateJSXFragment(node, env);
    throw new Error(`Unknown JSX attribute value type: ${node.type}`);
  }

  evaluateJSXChildren(children, env) {
    const result = [];
    for (const child of children) {
      if (child.type === 'JSXText') {
        const text = this.normalizeJSXText(child.value);
        if (text) result.push(text);
      } else if (child.type === 'JSXExpressionContainer') {
        if (child.expression.type !== 'JSXEmptyExpression') {
          const value = this.evaluate(child.expression, env);
          if (Array.isArray(value)) {
            result.push(...value);
          } else if (value !== null && value !== undefined && value !== false) {
            result.push(value);
          }
        }
      } else if (child.type === 'JSXElement') {
        result.push(this.evaluateJSXElement(child, env));
      } else if (child.type === 'JSXFragment') {
        result.push(this.evaluateJSXFragment(child, env));
      }
    }
    return result;
  }

  normalizeJSXText(text) {
    // React's JSX whitespace normalization
    const lines = text.split('\n');
    const normalized = lines
      .map((line, i) => {
        let result = line;
        if (i === 0) result = result.trimStart();
        if (i === lines.length - 1) result = result.trimEnd();
        return result;
      })
      .filter(line => line.length > 0)
      .join(' ');
    return normalized || null;
  }

  getCreateElement(env) {
    // Try React.createElement first
    try {
      const React = env.get('React');
      if (React && React.createElement) {
        return React.createElement.bind(React);
      }
    } catch (e) { /* not defined */ }

    // Try standalone createElement
    try {
      return env.get('createElement');
    } catch (e) { /* not defined */ }

    // Fallback: simple element factory for non-React usage
    return (type, props, ...children) => ({
      $$typeof: Symbol.for('react.element'),
      type,
      props: {
        ...props,
        children: children.length === 0 ? undefined : children.length === 1 ? children[0] : children
      },
      key: props?.key ?? null,
      ref: props?.ref ?? null
    });
  }

  getFragment(env) {
    // Try React.Fragment
    try {
      const React = env.get('React');
      if (React && React.Fragment) {
        return React.Fragment;
      }
    } catch (e) { /* not defined */ }

    // Try standalone Fragment
    try {
      return env.get('Fragment');
    } catch (e) { /* not defined */ }

    // Fallback: Symbol for fragments
    return Symbol.for('react.fragment');
  }

  // ===== Async JSX Support =====

  async evaluateJSXElementAsync(node, env) {
    const checkpointPromise = this._getCheckpointPromise(node, env);
    if (checkpointPromise) await checkpointPromise;

    const createElement = this.getCreateElement(env);
    const { type, props } = await this.evaluateJSXOpeningElementAsync(node.openingElement, env);
    const children = await this.evaluateJSXChildrenAsync(node.children, env);

    if (children.length === 0) {
      return createElement(type, props);
    } else if (children.length === 1) {
      return createElement(type, props, children[0]);
    }
    return createElement(type, props, ...children);
  }

  async evaluateJSXFragmentAsync(node, env) {
    const checkpointPromise = this._getCheckpointPromise(node, env);
    if (checkpointPromise) await checkpointPromise;

    const createElement = this.getCreateElement(env);
    const Fragment = this.getFragment(env);
    const children = await this.evaluateJSXChildrenAsync(node.children, env);

    if (children.length === 0) {
      return createElement(Fragment, null);
    } else if (children.length === 1) {
      return createElement(Fragment, null, children[0]);
    }
    return createElement(Fragment, null, ...children);
  }

  async evaluateJSXOpeningElementAsync(node, env) {
    const type = this.evaluateJSXElementName(node.name, env);
    const props = {};

    for (const attr of node.attributes) {
      if (attr.type === 'JSXAttribute') {
        const name = attr.name.type === 'JSXIdentifier'
          ? attr.name.name
          : `${attr.name.namespace.name}:${attr.name.name.name}`;
        const value = attr.value
          ? await this.evaluateJSXAttributeValueAsync(attr.value, env)
          : true;
        props[name] = value;
      } else if (attr.type === 'JSXSpreadAttribute') {
        Object.assign(props, await this.evaluateAsync(attr.argument, env));
      }
    }

    return { type, props: Object.keys(props).length > 0 ? props : null };
  }

  async evaluateJSXAttributeValueAsync(node, env) {
    if (node.type === 'Literal') return node.value;
    if (node.type === 'JSXExpressionContainer') {
      return await this.evaluateAsync(node.expression, env);
    }
    if (node.type === 'JSXElement') return await this.evaluateJSXElementAsync(node, env);
    if (node.type === 'JSXFragment') return await this.evaluateJSXFragmentAsync(node, env);
    throw new Error(`Unknown JSX attribute value type: ${node.type}`);
  }

  async evaluateJSXChildrenAsync(children, env) {
    const result = [];
    for (const child of children) {
      if (child.type === 'JSXText') {
        const text = this.normalizeJSXText(child.value);
        if (text) result.push(text);
      } else if (child.type === 'JSXExpressionContainer') {
        if (child.expression.type !== 'JSXEmptyExpression') {
          const value = await this.evaluateAsync(child.expression, env);
          if (Array.isArray(value)) {
            result.push(...value);
          } else if (value !== null && value !== undefined && value !== false) {
            result.push(value);
          }
        }
      } else if (child.type === 'JSXElement') {
        result.push(await this.evaluateJSXElementAsync(child, env));
      } else if (child.type === 'JSXFragment') {
        result.push(await this.evaluateJSXFragmentAsync(child, env));
      }
    }
    return result;
  }
}
